import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { ActionBar, DataTable, EmptyStateBox, StatusPill, TableShell, WorkspaceButton, type Tone } from './shared/CreditWorkspace';

export const Stack = styled.div`display: grid; gap: 1rem; min-width: 0;`;
export const Columns = styled.div`
  display: grid; grid-template-columns: minmax(0, 1.75fr) minmax(260px, 1fr); gap: 1rem; align-items: start;
  @media (max-width: 1000px) { grid-template-columns: 1fr; }
`;
export const Card = styled.section`
  min-width: 0; padding: 1rem; border: 1px solid rgba(148,163,184,.18); border-radius: 12px;
  background: rgba(8,16,30,.75);
  h3, h4 { margin: 0 0 .5rem; color: #f8fafc; font-size: .9rem; }
  p { margin: .4rem 0; white-space: pre-wrap; line-height: 1.55; }
`;
export const Small = styled.div`font-size: .73rem; line-height: 1.5; color: #94a3b8; overflow-wrap: anywhere;`;
export const Heading = styled.div`
  display: flex; flex-wrap: wrap; gap: .8rem; align-items: flex-start; justify-content: space-between;
  h2 { margin: 0 0 .35rem; color: #f8fafc; font-size: 1.15rem; }
`;
export const Facts = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(145px, 1fr)); gap: .7rem;
  > div { padding: .85rem; border-radius: 10px; background: rgba(15,23,42,.65); border: 1px solid rgba(148,163,184,.14); }
  strong { display: block; margin-top: .35rem; font-size: 1.05rem; color: #e2e8f0; font-variant-numeric: tabular-nums; }
`;
export const Field = styled.label`
  display: grid; gap: .4rem; color: #cbd5e1; font-size: .76rem; min-width: 0;
  input, select, textarea { width: 100%; box-sizing: border-box; min-width: 0; padding: .65rem .75rem;
    color: #f8fafc; background: #080f20; border: 1px solid #334155; border-radius: 8px; font: inherit; }
  textarea { min-height: 100px; resize: vertical; line-height: 1.5; }
  input:focus-visible, textarea:focus-visible, select:focus-visible { outline: 2px solid #38bdf8; outline-offset: 2px; }
`;
export const Fields = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: .9rem;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;
export const Notice = styled.div<{ $error?: boolean }>`
  padding: .8rem 1rem; border-radius: 10px; border: 1px solid ${p => p.$error ? '#9f4242' : '#226873'};
  background: ${p => p.$error ? '#381c25' : '#0b2933'}; color: ${p => p.$error ? '#fecaca' : '#a5f3fc'}; font-size: .8rem;
`;
export const Disclosure = styled.details`
  border: 1px solid rgba(148,163,184,.16); border-radius: 10px; padding: .85rem;
  summary { color: #cbd5e1; font-size: .8rem; cursor: pointer; font-weight: 700; }
  > div { margin-top: .75rem; }
`;
export const Stepper = styled.ol`
  display: flex; flex-wrap: wrap; list-style: none; padding: 0; margin: 0; gap: .5rem;
  li { padding: .55rem .85rem; border: 1px solid #334155; border-radius: 8px; font-size: .76rem; color: #94a3b8; }
  li[aria-current='step'] { color: #bae6fd; background: #123047; border-color: #38bdf8; }
`;
export const Table = styled(DataTable)`min-width: 640px; td { vertical-align: middle; }`;
export const LinkButton = styled.button`
  border: 0; padding: 0; background: transparent; color: #7dd3fc; text-align: left;
  cursor: pointer; font: inherit; font-weight: 700;
  &:hover { text-decoration: underline; }
  &:focus-visible { outline: 2px solid #7dd3fc; outline-offset: 3px; }
`;
export const human = (value: unknown) => String(value ?? '').replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase()) || 'Not recorded';
export const show = (value: unknown) => value == null || value === '' ? 'Not recorded' : String(value);
export const asRows = (value: any): any[] => {
  if (Array.isArray(value)) return value;
  try { const parsed = JSON.parse(value || '[]'); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
};
export const tone = (value: unknown): Tone => {
  const s = String(value || '').toUpperCase();
  if (['CRITICAL', 'HIGH', 'FAILED', 'REJECTED', 'BREACH'].some(v => s.includes(v))) return 'red';
  if (['APPROVED', 'VALIDATED', 'RESOLVED', 'PASS'].includes(s)) return 'emerald';
  if (['DRAFT', 'MEDIUM', 'MODERATE', 'OPEN', 'WATCHLIST', 'PENDING_REVIEW'].includes(s)) return 'amber';
  return 'cyan';
};
export const Badge = ({ value }: { value: unknown }) => <StatusPill $tone={tone(value)}>{human(value)}</StatusPill>;
export const Action = ({ label, reason, onClick, primary = false }: { label: string; reason?: string; onClick: () => void; primary?: boolean }) => (
  <div><WorkspaceButton $variant={primary ? 'primary' : undefined} disabled={Boolean(reason)} title={reason} onClick={onClick}>{label}</WorkspaceButton>
    {reason && <Small style={{ maxWidth: 260, marginTop: 4 }}>{reason}</Small>}
  </div>
);
export const History = ({ rows, title = 'Activity history' }: { rows: any[]; title?: string }) => (
  <Disclosure><summary>{title} · {rows.length} loaded</summary><div>
    {rows.length ? <TableShell><Table><thead><tr><th>Action</th><th>Status</th><th>By / when</th><th>Notes</th></tr></thead>
      <tbody>{rows.map((r, i) => <tr key={r.id ?? i}><td>{human(r.action || r.eventType || r.assessmentStatus)}</td>
        <td>{r.fromStatus ? `${human(r.fromStatus)} → ` : ''}{human(r.toStatus || r.assessmentStatus)}</td>
        <td>{show(r.actor || r.createdBy || r.scoredBy)}<Small>{show(r.createdAt || r.scoredAt)}</Small></td><td>{show(r.notes || r.approvalNotes)}</td></tr>)}</tbody>
    </Table></TableShell> : <EmptyStateBox>No recorded activity is available.</EmptyStateBox>}
  </div></Disclosure>
);

export interface FormField {
  key: string; label: string; type?: 'text' | 'textarea' | 'date' | 'datetime-local' | 'number' | 'select';
  options?: { value: string; label: string }[]; required?: boolean; min?: number; max?: number; hint?: string;
}
export interface Editor {
  tab: string; title: string; description: string; submitLabel: string; fields: FormField[];
  values: Record<string, string>; initial: string;
  submit: (values: Record<string, string>) => Promise<void>;
}
export const WorkflowEditor = ({ editor, busy, error, onChange, onCancel, onSubmit }: {
  editor: Editor; busy: boolean; error?: string; onChange: (key: string, value: string) => void; onCancel: () => void; onSubmit: () => void;
}) => {
  const editorRef = useRef<HTMLElement>(null);
  useEffect(() => {
    editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    editorRef.current?.querySelector<HTMLElement>('input, select, textarea, button')?.focus({ preventScroll: true });
  }, []);
  return <Card ref={editorRef} aria-label={editor.title}>
  <form onSubmit={e => { e.preventDefault(); onSubmit(); }}>
    <Stack><Heading><div><h3>{editor.title}</h3><Small>{editor.description}</Small></div>
      <Badge value={JSON.stringify(editor.values) === editor.initial ? 'Review' : 'Unsaved changes'} /></Heading>
      <Fields>{editor.fields.map(f => <Field key={f.key} style={f.type === 'textarea' ? { gridColumn: '1 / -1' } : undefined}>
        {f.label}{f.required ? ' *' : ''}
        {f.type === 'textarea' ? <textarea value={editor.values[f.key] || ''} disabled={busy} required={f.required} onChange={e => onChange(f.key, e.target.value)} />
          : f.type === 'select' ? <select value={editor.values[f.key] || ''} disabled={busy} required={f.required} onChange={e => onChange(f.key, e.target.value)}>
            <option value="">Select…</option>{f.options?.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select> : <input type={f.type || 'text'} value={editor.values[f.key] || ''} disabled={busy} required={f.required}
            min={f.min} max={f.max} step={f.type === 'number' ? 'any' : undefined} onChange={e => onChange(f.key, e.target.value)} />}
        {f.hint && <Small>{f.hint}</Small>}
      </Field>)}</Fields>
      {error && <Notice role="alert" $error>{error}</Notice>}
      <ActionBar><WorkspaceButton $variant="primary" disabled={busy} onClick={e => e.currentTarget.form?.requestSubmit()}>{busy ? 'Saving…' : editor.submitLabel}</WorkspaceButton>
        <WorkspaceButton onClick={onCancel} disabled={busy}>Cancel</WorkspaceButton></ActionBar>
    </Stack>
  </form>
</Card>;
};
